import React, { useState, useEffect } from "react";
import { II } from "../../components/common/Icons";
import { useITAdminData } from "../../context/ITAdminContext";
import "../../assets/styles/ITAdmin/ITADashPg.css";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

function getToken() {
  const role = sessionStorage.getItem("activeRole");
  if (role) return localStorage.getItem("token_" + role);
  for (const r of ["itadmin","superadmin","hr","employee","pm","intern"]) {
    const t = localStorage.getItem("token_" + r);
    if (t) return t;
  }
  return null;
}

async function apiCall(path, opts = {}) {
  const token = getToken();
  const headers = { ...(opts.headers||{}), "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  const text = await res.text();
  try { return JSON.parse(text); } catch { return {}; }
}

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; }
};

// ── Warranty Widget Modal ──
function WarrantyWidget({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doneing, setDoneing] = useState(null);

  useEffect(() => {
    apiCall("/itadmin/warranty-assets").then(data => {
      setItems(Array.isArray(data) ? data : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleDone = async (assetId) => {
    setDoneing(assetId);
    try {
      await apiCall("/itadmin/warranty-done", { method: "PUT", body: JSON.stringify({ assetId }) });
      setItems(prev => prev.filter(i => i.asset_id !== assetId));
    } catch (e) { alert(e.message); }
    setDoneing(null);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 680, maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,.25)" }}>
        {/* Header */}
        <div style={{ background: "linear-gradient(135deg,#7c3aed,#4f46e5)", color: "#fff", padding: "16px 20px", borderRadius: "14px 14px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>🛡️ Warranty Alerts</div>
            <div style={{ fontSize: 12, opacity: .8 }}>{items.length} asset(s) with warranty expiring within 90 days</div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.2)", border: "none", color: "#fff", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 18 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", flex: 1, padding: 16 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>Loading...</div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
              <div>No upcoming warranty expirations!</div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {items.map((a, i) => {
                const wDate = new Date(a.warranty_date);
                const daysLeft = Math.ceil((wDate - Date.now()) / 86400000);
                const expired = daysLeft < 0;
                return (
                  <div key={i} style={{ border: `1px solid ${expired ? "#fecaca" : "#e0e7ff"}`, borderRadius: 10, padding: "12px 14px", background: expired ? "#fef2f2" : "#f5f3ff" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                      <div>
                        <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 14 }}>{a.brand} <span style={{ fontWeight: 400, color: "#64748b", fontSize: 12 }}>({a.asset_id})</span></div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>{a.asset_type} · Tag: {a.asset_tag || "N/A"}</div>
                      </div>
                      <span style={{ background: expired ? "#dc2626" : "#7c3aed", color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                        {expired ? `Expired ${Math.abs(daysLeft)}d ago` : `${daysLeft}d left`}
                      </span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6, marginBottom: 10 }}>
                      <InfoCell label="Warranty Date" val={fmtDate(a.warranty_date)} />
                      <InfoCell label="Assigned To" val={a.emp_display_name || "—"} />
                      <InfoCell label="Designation" val={a.designation || "—"} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <button
                        onClick={() => handleDone(a.asset_id)}
                        disabled={doneing === a.asset_id}
                        style={{ background: "#059669", color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                        {doneing === a.asset_id ? "Marking..." : "✔ Done"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Maintenance Widget Modal ──
function MaintenanceWidget({ assets, onClose, onDone }) {
  const [doing, setDoing] = useState(null);
  const [nextDates, setNextDates] = useState({});
  const now = new Date().toISOString().split("T")[0];

  const overdue = assets.filter(a => a.next_maintenance_date && a.next_maintenance_date < now);
  const dueToday = assets.filter(a => a.next_maintenance_date === now);
  const upcoming = assets.filter(a => {
    if (!a.next_maintenance_date || a.next_maintenance_date <= now) return false;
    const d = new Date(a.next_maintenance_date);
    const diff = (d - Date.now()) / 86400000;
    return diff <= 30; // next 30 days
  });

  const handleMarkDone = async (asset) => {
    const nd = nextDates[asset.asset_id];
    if (!nd) { alert("Please set the next maintenance date first"); return; }
    setDoing(asset.asset_id);
    try {
      await apiCall("/itadmin/maintenance", {
        method: "PUT",
        body: JSON.stringify({ assetId: asset.asset_id, nextMaintenanceDate: nd })
      });
      // Dispatch event so dashboard KPI refreshes
      window.dispatchEvent(new CustomEvent("maintenance:updated"));
      onDone(asset.asset_id, nd);
    } catch(e) { alert("Error: " + e.message); }
    setDoing(null);
  };

  const Section = ({ title, items, color, bg }) => {
    if (!items.length) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8, padding: "4px 10px", background: bg, borderRadius: 6, display: "inline-block" }}>{title} ({items.length})</div>
        {items.map((a, i) => {
          const daysLeft = Math.ceil((new Date(a.next_maintenance_date) - Date.now()) / 86400000);
          return (
            <div key={i} style={{ border: `1px solid ${color}33`, borderRadius: 10, padding: "10px 12px", marginBottom: 8, background: bg + "44" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{a.brand} <span style={{ fontWeight: 400, color: "#64748b", fontSize: 11 }}>({a.asset_id})</span></div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{a.asset_type || a.type} · {a.emp_name || "Unassigned"}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>Last done: {fmtDate(a.last_maintenance_date) || "—"}</div>
                </div>
                <span style={{ background: color, color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                  {daysLeft < 0 ? `Overdue ${Math.abs(daysLeft)}d` : daysLeft === 0 ? "Due Today" : `In ${daysLeft}d`}
                </span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 3 }}>Set next maintenance date:</div>
                  <input type="date" min={new Date().toISOString().split("T")[0]}
                    value={nextDates[a.asset_id] || ""}
                    onChange={e => setNextDates(prev => ({...prev, [a.asset_id]: e.target.value}))}
                    style={{ width: "100%", padding: "6px 8px", border: "1.5px solid " + color + "66", borderRadius: 6, fontSize: 12, outline: "none" }} />
                </div>
                <button onClick={() => handleMarkDone(a)} disabled={doing === a.asset_id || !nextDates[a.asset_id]}
                  style={{ padding: "8px 14px", background: doing === a.asset_id || !nextDates[a.asset_id] ? "#e5e7eb" : "#059669", color: doing === a.asset_id || !nextDates[a.asset_id] ? "#9ca3af" : "#fff", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: doing === a.asset_id || !nextDates[a.asset_id] ? "not-allowed" : "pointer", whiteSpace: "nowrap", marginTop: 18 }}>
                  {doing === a.asset_id ? "Saving..." : "✔ Mark Done"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 700, maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,.25)" }}>
        <div style={{ background: "linear-gradient(135deg,#ef4444,#dc2626)", color: "#fff", padding: "16px 20px", borderRadius: "14px 14px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>🔧 Maintenance Due</div>
            <div style={{ fontSize: 12, opacity: .8, marginTop: 2 }}>
              {overdue.length > 0 && `${overdue.length} overdue · `}
              {dueToday.length > 0 && `${dueToday.length} due today · `}
              {upcoming.length > 0 && `${upcoming.length} upcoming (30 days)`}
              {assets.length === 0 && "All clear!"}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.2)", border: "none", color: "#fff", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 18 }}>×</button>
        </div>
        <div style={{ overflowY: "auto", flex: 1, padding: "16px 20px" }}>
          {assets.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>All assets are up to date!</div>
              <div style={{ fontSize: 13, marginTop: 6, color: "#94a3b8" }}>No maintenance due at this time.</div>
            </div>
          ) : (
            <>
              <Section title="🔴 Overdue" items={overdue} color="#dc2626" bg="#fef2f2" />
              <Section title="🟡 Due Today" items={dueToday} color="#d97706" bg="#fffbeb" />
              <Section title="🔵 Upcoming (30 days)" items={upcoming} color="#2563eb" bg="#eff6ff" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoCell({ label, val }) {
  return (
    <div style={{ background: "#fff", borderRadius: 6, padding: "6px 8px" }}>
      <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{val || "—"}</div>
    </div>
  );
}


// ─────────────── PIE CHART ENGINE ───────────────
const PRIORITY_COLORS = { Critical:"#ef4444", High:"#f97316", Medium:"#eab308", Low:"#22c55e" };
const STATUS_COLORS   = { open:"#6366f1", in_progress:"#f59e0b", resolved:"#10b981", closed:"#94a3b8", pending:"#f97316", approved:"#22c55e", rejected:"#ef4444" };
const TYPE_COLORS     = ["#6366f1","#f59e0b","#10b981","#ef4444","#3b82f6","#ec4899","#14b8a6","#a855f7","#f97316"];

function PieChart({ slices, label, colorMap }) {
  const [hovered, setHovered] = React.useState(null);
  const total = slices.reduce((s, d) => s + Number(d.count), 0);
  if (total === 0) return <div style={{textAlign:"center",padding:"40px 0",color:"#94a3b8",fontSize:13}}>No data</div>;

  const cx = 100, cy = 100, R = 80, r = 44;
  let angle = -Math.PI / 2;
  const built = slices.map((d, i) => {
    const val = Number(d.count);
    const sweep = (val / total) * 2 * Math.PI;
    const a0 = angle; angle += sweep; const a1 = angle;
    const isHov = hovered === i;
    const rr = isHov ? R + 8 : R;
    const large = sweep > Math.PI ? 1 : 0;
    const x = (a, rr) => cx + rr * Math.cos(a);
    const y = (a, rr) => cy + rr * Math.sin(a);
    const path = `M${x(a0,r)} ${y(a0,r)} L${x(a0,rr)} ${y(a0,rr)} A${rr} ${rr} 0 ${large} 1 ${x(a1,rr)} ${y(a1,rr)} L${x(a1,r)} ${y(a1,r)} A${r} ${r} 0 ${large} 0 ${x(a0,r)} ${y(a0,r)}Z`;
    const name = d.priority || d.status || d.asset_type || d.name || "—";
    const color = colorMap ? (colorMap[name] || TYPE_COLORS[i % TYPE_COLORS.length]) : TYPE_COLORS[i % TYPE_COLORS.length];
    return { path, color, val, name, pct: val/total, isHov };
  });

  const hov = hovered !== null ? built[hovered] : null;

  return (
    <div>
      <svg width="200" height="200" style={{display:"block",margin:"0 auto",overflow:"visible"}}>
        {built.map((s, i) => (
          <path key={i} d={s.path} fill={s.color} stroke="#fff" strokeWidth={2}
            style={{cursor:"pointer", transition:"all 0.22s cubic-bezier(.34,1.56,.64,1)",
              filter: s.isHov ? `drop-shadow(0 3px 10px ${s.color}99)` : "none"}}
            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)} />
        ))}
        {hov ? (
          <>
            <text x={cx} y={cy-9} textAnchor="middle" fontSize={20} fontWeight={800} fill="#0f172a">{hov.val}</text>
            <text x={cx} y={cy+7} textAnchor="middle" fontSize={9} fill="#64748b">{hov.name}</text>
            <text x={cx} y={cy+20} textAnchor="middle" fontSize={11} fontWeight={700} fill={hov.color}>{(hov.pct*100).toFixed(1)}%</text>
          </>
        ) : (
          <>
            <text x={cx} y={cy-6} textAnchor="middle" fontSize={22} fontWeight={800} fill="#0f172a">{total}</text>
            <text x={cx} y={cy+11} textAnchor="middle" fontSize={10} fill="#94a3b8">{label}</text>
          </>
        )}
      </svg>
      <div style={{display:"flex",flexWrap:"wrap",gap:"4px 10px",justifyContent:"center",marginTop:12}}>
        {built.map((s, i) => (
          <div key={i} style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",
            opacity: hovered===null||hovered===i ? 1 : 0.35, transition:"opacity 0.18s",
            background: hovered===i ? s.color+"18" : "transparent", borderRadius:6, padding:"2px 7px 2px 3px"}}
            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
            <div style={{width:8,height:8,borderRadius:"50%",background:s.color,flexShrink:0}}/>
            <span style={{fontSize:10,color:"#475569",fontWeight:500}}>{s.name} <b style={{color:s.color}}>({s.val})</b></span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, icon, children, delay, visible }) {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    if (visible) { const t = setTimeout(() => setShow(true), delay); return () => clearTimeout(t); }
  }, [visible, delay]);
  return (
    <div style={{background:"#fff", borderRadius:14, border:"1px solid #f1f5f9",
      boxShadow:"0 2px 16px rgba(0,0,0,.06)", padding:"18px 16px",
      opacity: show?1:0, transform: show?"translateY(0)":"translateY(22px)",
      transition:"opacity 0.5s ease, transform 0.5s cubic-bezier(.34,1.2,.64,1)"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
        <div style={{width:32,height:32,borderRadius:9,
          background:"linear-gradient(135deg,#4f46e5,#7c3aed)",
          display:"flex",alignItems:"center",justifyContent:"center",fontSize:15}}>{icon}</div>
        <div>
          <div style={{fontWeight:700,fontSize:13,color:"#0f172a"}}>{title}</div>
          <div style={{fontSize:10,color:"#94a3b8",marginTop:1}}>{subtitle}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

function AnalyticsCharts({ analytics, visible }) {
  if (!analytics) return null;
  const tp = analytics.ticketPriority || [];
  const ts = analytics.ticketStatus   || [];
  const tt = analytics.totalByType    || [];
  const av = analytics.availableByType|| [];
  return (
    <div style={{marginTop:24}}>
      <div style={{fontWeight:700,fontSize:15,color:"#0f172a",marginBottom:14,
        display:"flex",alignItems:"center",gap:8}}>
        <span style={{fontSize:18}}>📊</span> Analytics Overview
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
        <ChartCard icon="🎯" title="Ticket Priority" subtitle="By priority level" delay={0} visible={visible}>
          <PieChart slices={tp} label="Tickets" colorMap={PRIORITY_COLORS}/>
        </ChartCard>
        <ChartCard icon="📋" title="Ticket Status" subtitle="Current resolution status" delay={120} visible={visible}>
          <PieChart slices={ts} label="Tickets" colorMap={STATUS_COLORS}/>
        </ChartCard>
        <ChartCard icon="💻" title="Total Assets" subtitle="All assets by type" delay={240} visible={visible}>
          <PieChart slices={tt} label="Assets"/>
        </ChartCard>
        <ChartCard icon="✅" title="Available Assets" subtitle="Ready to assign, by type" delay={360} visible={visible}>
          <PieChart slices={av} label="Available"/>
        </ChartCard>
      </div>
    </div>
  );
}

function ITADashPg() {
  const { itaPeople, itaAssets, itaCreds, itaReqs, itaActionLogs } = useITAdminData();
  const [showWarranty, setShowWarranty] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [warrantyCount, setWarrantyCount] = useState(0);
  const [maintenanceDue, setMaintenanceDue] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [chartsVisible, setChartsVisible] = useState(false);

  const { refreshITAdminData } = useITAdminData();

  useEffect(() => {
    apiCall("/itadmin/dashboard").then(data => {
      if (data.warrantyCount !== undefined) setWarrantyCount(data.warrantyCount);
      if (data.maintenanceDue !== undefined) setMaintenanceDue(data.maintenanceDue);
    }).catch(() => {});
    apiCall("/itadmin/analytics").then(data => {
      setAnalytics(data);
      setTimeout(() => setChartsVisible(true), 300);
    }).catch(() => {});
  }, []);

  // Listen for maintenance updates from assets page
  useEffect(() => {
    const handleMaintenanceUpdate = () => {
      refreshITAdminData().catch(() => {});
      apiCall("/itadmin/dashboard").then(data => {
        if (data.maintenanceDue !== undefined) setMaintenanceDue(data.maintenanceDue);
      }).catch(() => {});
    };
    window.addEventListener("maintenance:updated", handleMaintenanceUpdate);
    return () => window.removeEventListener("maintenance:updated", handleMaintenanceUpdate);
  }, [refreshITAdminData]);

  const totalAssets = itaAssets.length;
  const assigned = itaAssets.filter(a => a.status === "Assigned").length;
  const available = itaAssets.filter(a => a.status === "Available").length;
  const pendingReqs = itaReqs.filter(r => r.status === "pending").length;
  const exitPeople = itaPeople.filter(p => p.status === "Exiting");

  const now = new Date().toISOString().split("T")[0];
  const plus30 = new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0];
  // Due or upcoming within 30 days
  const maintAssets = itaAssets.filter(a => a.next_maintenance_date && a.next_maintenance_date <= plus30);
  // Just overdue/today for count badge
  const maintDueNow = itaAssets.filter(a => a.next_maintenance_date && a.next_maintenance_date <= now);

  const kpis = [
    { l: "Total Assets", v: String(totalAssets), ch: `${assigned} assigned`, cl: "#2563eb" },
    { l: "Available", v: String(available), ch: "Ready to assign", cl: "#059669" },
    { l: "Pending Requests", v: String(pendingReqs), ch: "Action Required", cl: "#d97706" },
    { l: "Active Credentials", v: String(itaCreds.length), ch: "With access", cl: "#4338ca" },
    {
      l: "Warranty Alerts", v: String(warrantyCount), ch: "Expiring soon", cl: "#7c3aed",
      action: warrantyCount > 0 ? () => setShowWarranty(true) : null, actionLabel: "View"
    },
    { l: "Maintenance Due", v: String(maintDueNow.length || maintenanceDue), ch: maintAssets.length > maintDueNow.length ? `+${maintAssets.length - maintDueNow.length} in 30 days` : "Needs attention", cl: "#ef4444",
      action: maintAssets.length > 0 ? () => setShowMaintenance(true) : null, actionLabel: "View" },
  ];

  return (
    <div>
      <div className="sec">IT Command Center</div>

      {/* KPI Cards */}
      <div className="kpi">
        {kpis.map((k, i) => (
          <div key={i} className="kpi-c itadashpg-dyn-1" style={{ "--itadashpg-1-border-left": `3px solid ${k.cl}` }}>
            <div className="kpi-v itadashpg-dyn-2" style={{ "--itadashpg-2-color": k.cl }}>{k.v}</div>
            <div className="kpi-l">{k.l}</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div className="kpi-ch itadashpg-dyn-3" style={{ "--itadashpg-3-color": k.cl }}>{k.ch}</div>
              {k.action && (
                <button onClick={k.action} style={{ background: k.cl, color: "#fff", border: "none", borderRadius: 5, padding: "2px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>
                  {k.actionLabel}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="g2 mb16">
        {/* Pending Requests */}
        <div className="cd">
          <div className="cd-h"><div className="cd-t">Pending IT Requests</div><div className="cd-s">{pendingReqs} pending</div></div>
          <div className="cd-b itadashpg-inline-1">
            <table className="tbl">
              <thead><tr><th>ID</th><th>Employee</th><th>Type</th><th>Date</th><th>Status</th></tr></thead>
              <tbody>
                {itaReqs.filter(r => r.status === "pending").slice(0, 5).map((r, i) => (
                  <tr key={i}>
                    <td className="itadashpg-inline-2">{r.req_id || r.rid}</td>
                    <td className="itadashpg-inline-3">{r.emp_name || r.empName}</td>
                    <td>{r.type}</td>
                    <td className="itadashpg-inline-4">{r.date}</td>
                    <td><span className="chip chip-wn">pending</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="cd">
          <div className="cd-h"><div className="cd-t">Recent Activity</div></div>
          <div className="cd-b">
            {itaActionLogs.slice(0, 5).map((l, i) => (
              <div key={i} style={{ "--itadashpg-4-border-bottom": i < 4 ? "1px solid #f1f5f9" : "none" }} className="itadashpg-dyn-4">
                <div className="itadashpg-inline-5">{l.action} — {l.target}</div>
                <div className="itadashpg-inline-6">{l.admin_name || l.admin} · {l.timestamp_str || l.ts}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="g2 mb16">
        {/* Maintenance Due */}
        <div className="cd" style={{ cursor: maintAssets.length > 0 ? "pointer" : "default" }} onClick={() => maintAssets.length > 0 && setShowMaintenance(true)}>
          <div className="cd-h">
            <div className="cd-t">🔧 Maintenance Due</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div className="cd-s">{maintDueNow.length} overdue{maintAssets.length > maintDueNow.length ? ` · ${maintAssets.length - maintDueNow.length} upcoming` : ""}</div>
              {maintAssets.length > 0 && (
                <button onClick={e => { e.stopPropagation(); setShowMaintenance(true); }}
                  style={{ background: "#ef4444", color: "#fff", border: "none", borderRadius: 5, padding: "2px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>
                  Manage
                </button>
              )}
            </div>
          </div>
          <div className="cd-b">
            {maintAssets.length === 0 ? (
              <div className="itadashpg-inline-7" style={{ color: "#22c55e" }}>✅ No maintenance due</div>
            ) : (
              <>
                {maintDueNow.slice(0, 3).map((a, i) => (
                  <div key={i} className="pf" style={{ cursor: "pointer" }} onClick={() => setShowMaintenance(true)}>
                    <div>
                      <div className="itadashpg-inline-8">{a.brand || a.asset_id}</div>
                      <div className="itadashpg-inline-9">{a.asset_id} · {a.emp_name || "Unassigned"}</div>
                    </div>
                    <span className="chip chip-dn">
                      {new Date(a.next_maintenance_date) < new Date() ? `Overdue: ${fmtDate(a.next_maintenance_date)}` : `Due: ${fmtDate(a.next_maintenance_date)}`}
                    </span>
                  </div>
                ))}
                {maintAssets.length > 3 && (
                  <div style={{ textAlign: "center", padding: "8px 0", fontSize: 12, color: "#6366f1", fontWeight: 600, cursor: "pointer" }}
                    onClick={() => setShowMaintenance(true)}>
                    + {maintAssets.length - 3} more — Click to view all
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Exit Queue */}
        <div className="cd">
          <div className="cd-h">
            <div className="cd-t">Exit Queue — Access Review</div>
            <div className="cd-s">{exitPeople.length} exiting</div>
          </div>
          <div className="cd-b">
            {exitPeople.length === 0 ? (
              <div className="itadashpg-inline-10">No pending exits</div>
            ) : exitPeople.map((p, i) => (
              <div key={i} className="pf">
                <div>
                  <div className="itadashpg-inline-11">{p.name}</div>
                  <div className="itadashpg-inline-12">{p.id} · {p.dept}</div>
                </div>
                <span className="chip chip-wn">Revoke access</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── ANALYTICS CHARTS ── */}
      <AnalyticsCharts analytics={analytics} visible={chartsVisible} />

      {/* Warranty Widget Modal */}
      {showWarranty && <WarrantyWidget onClose={() => setShowWarranty(false)} />}

      {/* Maintenance Widget Modal */}
      {showMaintenance && (
        <MaintenanceWidget
          assets={maintAssets}
          onClose={() => setShowMaintenance(false)}
          onDone={(assetId, newNextDate) => {
            // Optimistically update local list so count drops immediately
            // Context will refresh on next fetchAll (triggered by asset update)
            setShowMaintenance(false);
            // Force context refresh to update KPI count
            setTimeout(() => {
              import("../../services/api").then(m => m.default.getITAssets()).then(data => {
                // Context will update via refreshITAdminData call in ITAAssetsPg
                // But we can trigger a page-level refresh here too
                window.dispatchEvent(new CustomEvent("maintenance:updated"));
              }).catch(() => {});
            }, 500);
          }}
        />
      )}
    </div>
  );
}

export default ITADashPg;
